import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { getMe, patchMe, type MemberAddress } from '../../lib/api';
import {
  AddressFields,
  regionNameForCode,
  validateAddress,
  type AddressErrors,
} from '../address-fields';

const EMPTY: MemberAddress = {};

export function AddressTab() {
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const [addr, setAddr] = useState<MemberAddress>(EMPTY);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<AddressErrors>({});

  useEffect(() => {
    if (meQ.data?.address) setAddr(meQ.data.address);
  }, [meQ.data]);

  const mut = useMutation({
    meta: { successMessage: 'Address saved.' },
    mutationFn: () => patchMe({ address: { ...addr, region: regionNameForCode(addr.regionCode) } }),
    onSuccess: () => {
      meQ.refetch();
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Shipping address</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Your region determines courier rates at checkout. Keep this up to date so shipping quotes
          are accurate.
        </p>
        <AddressFields
          value={addr}
          onChange={(next) => {
            setAddr(next);
            setErrors((prev) => (Object.keys(prev).length ? validateAddress(next) : prev));
          }}
          errors={errors}
        />
        <Button
          onClick={() => {
            const errs = validateAddress(addr);
            if (Object.keys(errs).length > 0) {
              setErrors(errs);
              return;
            }
            mut.mutate();
          }}
          disabled={mut.isPending}
          size="sm"
        >
          {mut.isPending ? 'Saving…' : saved ? 'Saved' : 'Save address'}
        </Button>
        {mut.error && <p className="text-xs text-destructive">Couldn't save. Try again.</p>}
      </CardContent>
    </Card>
  );
}
